import { supabase } from '../lib/supabase';
import type { CandidateFlight } from '../types';

export const SCHEDULE_LOOKUP_ENABLED = true;

interface LegQuery {
  index: number;
  origin: string;
  destination: string;
  date: string;
  airlineCode?: string;
}

export async function findCandidateFlights({
  origin,
  destination,
  date,
  airlineCode,
}: {
  origin: string;
  destination: string;
  date: string;
  airlineCode?: string;
}): Promise<CandidateFlight[]> {
  try {
    let query = supabase
      .from('flight_schedules')
      .select('id, flight_number, departure_time, arrival_time, aircraft_type')
      .eq('departure_airport', origin.toUpperCase())
      .eq('arrival_airport', destination.toUpperCase())
      .eq('date', date)
      .order('departure_time', { ascending: true });

    if (airlineCode) {
      query = query.eq('airline_code', airlineCode.toUpperCase());
    }

    const { data, error } = await query;

    if (error || !data) return [];

    return data.map((row) => ({
      id: row.id,
      flightNumber: row.flight_number,
      departureTime: row.departure_time ?? '',
      arrivalTime: row.arrival_time ?? '',
      ...(row.aircraft_type ? { aircraftType: row.aircraft_type } : {}),
    }));
  } catch {
    return [];
  }
}

export async function findCandidatesForLegs(
  legs: LegQuery[]
): Promise<Map<number, CandidateFlight[]>> {
  const results = await Promise.all(
    legs.map(async (leg) => {
      const candidates = await findCandidateFlights({
        origin: leg.origin,
        destination: leg.destination,
        date: leg.date,
        airlineCode: leg.airlineCode,
      });
      return { index: leg.index, candidates };
    })
  );

  const map = new Map<number, CandidateFlight[]>();
  for (const { index, candidates } of results) {
    map.set(index, candidates);
  }
  return map;
}
